import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isCurrentUserEmailVerified } from '../../services/auth';
import { InviteShell } from '../auth/AcceptInviteLayout';
import type { SignupLookupResponse, SignupSport } from '../../types/signup';
import { SignupEmailStep } from './SignupEmailStep';
import {
  SignupAccountStep,
  type SignupAccountCreated,
  type SignupAccountInitialValues,
} from './SignupAccountStep';
import { SignupVerifyStep } from './SignupVerifyStep';
import { SignupOutcome, type SignupOutcomeState } from './SignupOutcome';
import { clearSignup, loadSignup, saveSignup } from './signupStorage';

/**
 * Public self-serve signup: email lookup, account, email verification, then payment.
 *
 * Public on purpose, like `/accept-invite`: the coach has no account when they arrive. The price and
 * tier are never chosen here. The lookup reports what the server derived from the email domain, and
 * the create call sends only a name and a sport.
 *
 * Payment is not a step of this page. Once the program exists the coach is a signed-in admin of an
 * organization awaiting payment, and `/signup/payment` (also Stripe's cancel URL, and where a
 * returning unpaid admin is sent) owns the checkout.
 */

type SignupView =
  | { phase: 'email'; email?: string }
  | {
      phase: 'account';
      email: string;
      lookup: SignupLookupResponse;
      /** The form as it was, when the coach is signing back in to a signup already under way. */
      initialValues?: SignupAccountInitialValues;
    }
  | {
      phase: 'verify';
      email: string;
      lookup: SignupLookupResponse;
      firstName: string;
      lastName: string;
      sport: SignupSport;
      alreadyVerified: boolean;
      verificationSendFailed: boolean;
    }
  | { phase: 'outcome'; outcome: SignupOutcomeState };

export const SignupPage = () => {
  const { user, logout, organizationAwaitingPayment } = useAuth();

  /**
   * A refresh on the verify step (or a second tab, since the form is kept in localStorage) resumes
   * it, but only for the Firebase account it was started with and only while that account has no
   * RosterPilot profile yet. Read during the first render, when auth has already settled
   * (`AppRoutes` renders no route before it does).
   */
  const [resumed] = useState(() =>
    user && !user.profile ? loadSignup(user.email) : null
  );
  const [view, setView] = useState<SignupView>(() =>
    resumed
      ? {
          phase: 'verify',
          email: resumed.email,
          lookup: resumed.lookup,
          firstName: resumed.firstName,
          lastName: resumed.lastName,
          sport: resumed.sport,
          // Verified in another tab (or before a refresh): nothing to wait for, so the step checks
          // straight away instead of asking for another click.
          alreadyVerified: isCurrentUserEmailVerified(),
          // Nothing is known about the last send; the cooldown is the safe default.
          verificationSendFailed: false,
        }
      : // A Firebase-only session we cannot resume is signed out below; keep its address so the
        // coach can pick the signup up again (the account step signs back in to that account).
        { phase: 'email', email: user && !user.profile ? (user.email ?? undefined) : undefined }
  );

  /**
   * A Firebase-only session that is not a signup we can resume is signed out, as AcceptInvite does:
   * through AuthContext, so per-user caches are cleared too. A coach coming back to an abandoned
   * signup is picked up again by the account step when they re-enter the same email and password.
   * A session we *can* resume (this tab's refresh, or another tab's signup) is left alone: signing
   * it out would sign out every tab.
   *
   * Mount only: after this, the page itself creates the session it would otherwise be signing out.
   */
  useEffect(() => {
    if (user && !user.profile && !resumed) {
      clearSignup();
      void logout().catch(() => {
        // Creating the new account replaces the session anyway.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOver = useCallback(() => {
    clearSignup();
    if (user) {
      void logout().catch(() => {
        // A stale session is replaced by the next account creation.
      });
    }
    setView({ phase: 'email' });
  }, [user, logout]);

  const handleLookup = (email: string, lookup: SignupLookupResponse) => {
    switch (lookup.status) {
      case 'eligible':
        setView({ phase: 'account', email, lookup });
        return;
      case 'unverified':
        setView({ phase: 'outcome', outcome: { phase: 'unverified' } });
        return;
      default:
        setView({ phase: 'outcome', outcome: { phase: 'unavailable' } });
    }
  };

  const handleCreated = (email: string, lookup: SignupLookupResponse, created: SignupAccountCreated) => {
    saveSignup({
      step: 'verify',
      email,
      firstName: created.firstName,
      lastName: created.lastName,
      sport: created.sport,
      lookup,
    });
    setView({ phase: 'verify', email, lookup, ...created });
  };

  // Someone who is already a RosterPilot user has nothing to sign up for: an unpaid admin goes to
  // payment, anyone else into the app, the same way `/login` bounces a signed-in user.
  if (user?.profile) {
    return <Navigate to={organizationAwaitingPayment ? '/signup/payment' : '/players'} replace />;
  }

  return (
    <InviteShell>
      {view.phase === 'email' && (
        <SignupEmailStep initialEmail={view.email} onResult={handleLookup} />
      )}

      {view.phase === 'account' && (
        <SignupAccountStep
          email={view.email}
          lookup={view.lookup}
          onCreated={(created) => handleCreated(view.email, view.lookup, created)}
          initialValues={view.initialValues}
          onEmailInUse={() =>
            setView({
              phase: 'outcome',
              outcome: {
                phase: 'signInInstead',
                email: view.email,
                message: null,
                passwordHelp: true,
              },
            })
          }
          onChangeEmail={() => setView({ phase: 'email', email: view.email })}
        />
      )}

      {view.phase === 'verify' && (
        <SignupVerifyStep
          email={view.email}
          firstName={view.firstName}
          lastName={view.lastName}
          sport={view.sport}
          alreadyVerified={view.alreadyVerified}
          verificationSendFailed={view.verificationSendFailed}
          onOutcome={(outcome) => setView({ phase: 'outcome', outcome })}
          onSignInAgain={() =>
            setView({
              phase: 'account',
              email: view.email,
              lookup: view.lookup,
              initialValues: {
                firstName: view.firstName,
                lastName: view.lastName,
                sport: view.sport,
              },
            })
          }
          onStartOver={startOver}
        />
      )}

      {view.phase === 'outcome' && <SignupOutcome outcome={view.outcome} onStartOver={startOver} />}
    </InviteShell>
  );
};
